/* @jsx h */
import { defineComponent, h } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import type { NotificationSettings } from "@lemmonhq/types";

interface NotificationsSettingsProps {
  settings: NotificationSettings;
  onUpdate: (settings: NotificationSettings) => void;
}

export const NotificationsSettingsComponent =
  defineComponent<NotificationsSettingsProps>({
    name: "NotificationsSettings",
    props: ["settings", "onUpdate"],
    setup(props) {
      const { t } = useLocalization();

      const getNotificationEvents = () => [
        {
          name: "on_reservation_request_received",
          label: t("settings.notifications.new_reservation"),
          description: t("settings.notifications.new_reservation_desc"),
          icon: "notifications",
        },
      ];

      const getNotificationItem = (name: string) => {
        return (
          props.settings.items.find((item) => item.name === name) || {
            name,
            channels: { email: false, whatsapp: false, text: false },
          }
        );
      };

      const updateChannel = (
        name: string,
        channel: "email" | "whatsapp" | "text",
        enabled: boolean,
      ) => {
        const items = [...props.settings.items];
        const existingIndex = items.findIndex((item) => item.name === name);

        if (existingIndex >= 0) {
          items[existingIndex] = {
            ...items[existingIndex],
            channels: {
              ...items[existingIndex].channels,
              [channel]: enabled,
            },
          };
        } else {
          items.push({
            name,
            channels: {
              email: channel === "email" ? enabled : false,
              whatsapp: channel === "whatsapp" ? enabled : false,
              text: channel === "text" ? enabled : false,
            },
          });
        }

        props.onUpdate({ items });
      };

      const ChannelToggle = ({
        label,
        icon,
        enabled,
        onToggle,
        disabled = false,
      }: {
        label: string;
        icon: string;
        enabled: boolean;
        onToggle: () => void;
        disabled?: boolean;
      }) => (
        <div class="flex items-center gap-2">
          <input
            type="checkbox"
            checked={enabled}
            disabled={disabled}
            class="toggle toggle-primary toggle-sm"
            onChange={onToggle}
          />
          <span class="material-symbols-outlined text-base-content/60 text-sm">
            {icon}
          </span>
          <span class="text-sm text-base-content/70">{label}</span>
        </div>
      );

      return () => (
        <div class="l-box-shadow rounded rounded-2xl shadow-xl bg-base-100 border border-base-100">
          <div class="card-body">
            <div class="flex items-center gap-3 mb-6">
              <div class="p-3 bg-info/10 rounded-xl">
                <span class="material-symbols-outlined text-info text-2xl">
                  notifications
                </span>
              </div>
              <div class="flex-1">
                <h3 class="text-lg font-semibold text-accent">
                  {t("settings.notifications.title")}
                </h3>
                <p class="text-sm text-base-content">
                  {t("settings.notifications.desc")}
                </p>
              </div>
            </div>

            <div class="space-y-3">
              {getNotificationEvents().map((event) => {
                const item = getNotificationItem(event.name);

                return (
                  <div
                    key={event.name}
                    class="border  rounded-lg p-4 hover:border-primary/50 transition-all"
                  >
                    <div class="flex items-start gap-4">
                      <div class="p-2 bg-primary/10 rounded-lg">
                        <span class="material-symbols-outlined text-primary text-xl">
                          {event.icon}
                        </span>
                      </div>

                      <div class="flex-1 min-w-0">
                        <h4 class="font-semibold text-base-content mb-1">
                          {event.label}
                        </h4>
                        <p class="text-sm text-base-content/60 mb-4">
                          {event.description}
                        </p>

                        <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <ChannelToggle
                            label={t("settings.notifications.channel_email")}
                            icon="email"
                            enabled={item.channels.email}
                            onToggle={() =>
                              updateChannel(
                                event.name,
                                "email",
                                !item.channels.email,
                              )
                            }
                          />
                          <ChannelToggle
                            label={t("settings.notifications.channel_whatsapp")}
                            icon="chat"
                            enabled={item.channels.whatsapp}
                            onToggle={() =>
                              updateChannel(
                                event.name,
                                "whatsapp",
                                !item.channels.whatsapp,
                              )
                            }
                            disabled={true}
                          />
                          <ChannelToggle
                            label={t("settings.notifications.channel_sms")}
                            icon="sms"
                            enabled={item.channels.text}
                            onToggle={() =>
                              updateChannel(
                                event.name,
                                "text",
                                !item.channels.text,
                              )
                            }
                            disabled={true}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      );
    },
  });
